//---------------------------------------------------------------------
// React, MUI, and other third-party modules imports
//---------------------------------------------------------------------
import React, { useState, useContext } from 'react';
import { makeStyles } from '@material-ui/core';

//---------------------------------------------------------------------
// Common helpers and constants imports
//---------------------------------------------------------------------
import { LayoutContext } from '../../common/contexts';
import { setClassNameInDefns } from '../../common/definitionHelper';

//---------------------------------------------------------------------
// Page specific helpers and constants imports
//---------------------------------------------------------------------
import { changeMemberPassword } from '../../common/services/empMySettingsServices';

//---------------------------------------------------------------------
// Common components imports
//---------------------------------------------------------------------
import AfitdTextField from '../../common/elemental/AfitdTextField';
import AfitdTypography from '../../common/elemental/AfitdTypography';
import AfitdEditDialog from '../../common/elemental/AfitdEditDialog';

//---------------------------------------------------------------------
// Page specific components imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Utilities (clsx, lodash, date-fns, etc.) imports
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// CSS
//---------------------------------------------------------------------
const useStyles = (params = {}) => {
    return makeStyles(theme => ({
        hidden: { display: 'none' },
        label: { fontWeight: 'bold', margin: '10px 0 5px 0' },
    }));
};

//---------------------------------------------------------------------
// Get definitions
//---------------------------------------------------------------------
const getDefns = params => {
    const { classes, props, data, setData, handleSaveButton, errors, handleCancelButton } = params;
    const { openChangePasswordModal } = props;

    const { password, confirmPassword } = data;
    const { passwordError, confirmPasswordError } = errors;
    const { hidden: hiddenCls } = classes;

    const handleChangeFields = evt => {
        const { name, value } = evt.target;
        setData({ ...data, [name]: value });
    };

    const isDirty = password;

    const defns = {
        container: {
            open: openChangePasswordModal,
            onClose: handleCancelButton,
            label: `Change Password`,
            actionButtonDefns: {
                save: { disabled: !isDirty, onClick: handleSaveButton, label: 'Change' },
            },
        },
        label: { variant: 'body2' },
        password: {
            type: 'password',
            onChange: handleChangeFields,
            name: 'password',
            value: password,
            placeholder: 'Password',
            error: passwordError,
        },
        confirmPassword: {
            type: 'password',
            disabled: !password,
            onChange: handleChangeFields,
            name: 'confirmPassword',
            value: confirmPassword,
            placeholder: 'Confirm Password',
            error: confirmPasswordError,
        },
        passwordError: { variant: 'body2', value: 'Password must be atleast 6 characters long.', className: !passwordError && hiddenCls },
        confirmPasswordError: { variant: 'body2', value: 'Password and Confirm Password must be same.', className: !confirmPasswordError && hiddenCls },
    };
    setClassNameInDefns(defns, classes);
    return defns;
};

//---------------------------------------------------------------------
// Helper functions
//---------------------------------------------------------------------

//---------------------------------------------------------------------
// Component
//---------------------------------------------------------------------
const CancelSubscriptionDialog = props => {
    //---------------------------------------------------------------------
    // From context and other hooks
    //---------------------------------------------------------------------
    const classes = useStyles(/*{param1, param2}*/)();

    const layoutContext = useContext(LayoutContext);
    const { setSnackbarMessage, setSnackbarErrorMessage } = layoutContext;

    //---------------------------------------------------------------------
    // From props
    //---------------------------------------------------------------------
    const { closeChangePasswordModal } = props;

    //---------------------------------------------------------------------
    // Local states
    //---------------------------------------------------------------------
    const [data, setData] = useState({ password: '', confirmPassword: '' });
    const { password, confirmPassword } = data;
    const [errors, setErrors] = useState({ passwordError: false, confirmPasswordError: false });

    //---------------------------------------------------------------------
    // Side effects
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // Derived
    //---------------------------------------------------------------------

    //---------------------------------------------------------------------
    // useCallback / Event handlers
    //---------------------------------------------------------------------
    const handleSaveButton = () => {
        if (password !== confirmPassword) setErrors({ passwordError: false, confirmPasswordError: true });
        if (password.length < 6) setErrors({ confirmPasswordError: false, passwordError: true });
        if (password === confirmPassword && password.length > 5) {
            changeMemberPassword(password, setSnackbarMessage, setSnackbarErrorMessage);
            setErrors({ passwordError: false, confirmPasswordError: false });
            setData({ password: '', confirmPassword: '' });
            closeChangePasswordModal();
        }
    };

    const handleCancelButton = () => {
        closeChangePasswordModal();
    };

    //---------------------------------------------------------------------
    // Definitions
    //---------------------------------------------------------------------
    const defns = getDefns({ classes, props, data, setData, errors, handleCancelButton, handleSaveButton });

    //---------------------------------------------------------------------
    // Render
    //---------------------------------------------------------------------
    return (
        <AfitdEditDialog {...defns.container}>
            <AfitdTypography {...defns.label}>New Password</AfitdTypography>
            <AfitdTextField {...defns.password} />
            <AfitdTypography {...defns.passwordError} />
            <AfitdTypography {...defns.label}>New Confirm Password</AfitdTypography>
            <AfitdTextField {...defns.confirmPassword} />
            <AfitdTypography {...defns.confirmPasswordError} />
        </AfitdEditDialog>
    );
};

export default CancelSubscriptionDialog;
